import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Search } from 'components/SearchBar/components';
import { useSelector, useDispatch } from 'react-redux';
import { ModelConfigurationProcessingStatus } from 'components';
import {
  Modal,
  Card,
  CardContent,
  CardActions,
  Button,
  CardHeader,
  Divider,
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Grid,
  Tabs,
  Tab,
  LinearProgress
} from '@material-ui/core';
import {
  findSelectModelModels,
  findSelectModelModelSets,
  setSelectModelOpen,
  setSelectModelSearch,
  setSelectModelTab
} from 'actions';
import { MODEL_SET_CALUCLATION_METHODS } from 'common/constants';
import ws from 'utils/ws';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 850,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  content: {
    padding: 0
  },
  actions: {
    justifyContent: 'flex-end'
  },
  search: {
    minWidth: 480,
    maxWidth: 480,
    flexBasis: 480
  },
  tableContainer: {
    minHeight: 431,
    maxHeight: 431,
    overflowY: 'auto'
  },
  searchContainer: {
    padding: theme.spacing(2)
  },
  buttonCell: {
    paddingTop: 0,
    paddingBottom: 0,
    width: 100
  },
  buttonExtraSmall: {
    padding: '1px 8px',
    fontSize: '0.8125rem'
  },
  labelCell: {
    paddingTop: 0,
    paddingBottom: 0
  },
  container: {
    maxHeight: 431
  }
}));

const SelectModelModal = props => {
  const {
    model,
    modelSetsSupported,
    tritonModelsSupported,
    onSelectModel,
    onSelectModelSet,
    onSelectTritonModel,
    excludedModelIds,
    excludedModelSetIds,
    excludedTritonIds,
    ...rest
  } = props;
  const {
    models,
    modelSets,
    open,
    tab,
    search,
    loadingModels,
    loadingModelSets
  } = useSelector(state => state.selectModel);

  const classes = useStyles();
  const dispatch = useDispatch();

  /**
   * Triton types are fetched inside this component rather than from Redux
   * because they are only ever consumed here. Each entry is
   * {typeName, recipe, column, versions: [{integerVersion, alias?}]}. The
   * flattened pickerRows list below pairs them with their version for the
   * row-per-version table.
   */
  const [eligibleTritonTypes, setEligibleTritonTypes] = useState([]);
  const [loadingTritonModels, setLoadingTritonModels] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (mounted && open) {
      dispatch(findSelectModelModels());
      dispatch(findSelectModelModelSets());
      if (tritonModelsSupported) {
        setLoadingTritonModels(true);
        ws.get('/triton_models/eligible')
          .then(response => {
            if (!mounted) return;
            setEligibleTritonTypes(response.data?.result || []);
          })
          .catch(() => {
            if (!mounted) return;
            /* Mongo hiccup on the backend already logs a warn; render empty. */
            setEligibleTritonTypes([]);
          })
          .finally(() => {
            if (mounted) setLoadingTritonModels(false);
          });
      }
    }

    return () => {
      mounted = false;
    };
  }, [open, tritonModelsSupported]);

  const findModelsByName = () => {
    return models.filter(model => {
      return (
        (!excludedModelIds || excludedModelIds.indexOf(model.id) < 0) &&
        (search === '' ||
          model.displayName.toUpperCase().indexOf(search.toUpperCase()) !== -1)
      );
    });
  };

  const findModelSetsByName = () => {
    return modelSets.filter(modelSet => {
      return (
        (!excludedModelSetIds ||
          excludedModelSetIds.indexOf(modelSet.id) < 0) &&
        (search === '' ||
          modelSet.name.toUpperCase().indexOf(search.toUpperCase()) !== -1)
      );
    });
  };

  /**
   * One row per (Triton type, version) that is not already in excludedTritonIds.
   * The exclude list is the shared "TRITON:<type>:v<ver>" string used server-side
   * as the ModelSet constituent identity, so the editor can pass the same key
   * for the current set's existing members.
   */
  const findTritonRows = () => {
    const needle = (search || '').toUpperCase();
    const rows = [];
    (eligibleTritonTypes || []).forEach(type => {
      (type.versions || []).forEach(version => {
        const identity =
          'TRITON:' + type.typeName + ':v' + version.integerVersion;
        if (excludedTritonIds && excludedTritonIds.indexOf(identity) >= 0)
          return;
        const alias = version.alias || null;
        const haystack =
          type.typeName.toUpperCase() +
          ' V' +
          version.integerVersion +
          (alias ? ' ' + alias.toUpperCase() : '');
        if (needle !== '' && haystack.indexOf(needle) < 0) return;
        rows.push({
          identity,
          typeName: type.typeName,
          version: version.integerVersion,
          alias,
          recipe: type.recipe,
          column: type.column
        });
      });
    });
    return rows;
  };

  if (!open) return null;

  const searchedModels = findModelsByName(models);
  const searchedModelSets = findModelSetsByName(modelSets);
  const searchedTritonRows = tritonModelsSupported ? findTritonRows() : [];

  const tabs = [{ value: 'models', label: 'Models' }];
  if (modelSetsSupported)
    tabs.push({ value: 'modelSets', label: 'Model Sets' });
  if (tritonModelsSupported)
    tabs.push({ value: 'triton', label: 'Triton Models' });

  const handleClose = () => {
    dispatch(setSelectModelOpen(false));
  };

  const handleSearchFieldChange = search => {
    dispatch(setSelectModelSearch(search?.trim()));
  };

  const handleSelectModel = model => {
    onSelectModel(model);
    handleClose();
  };

  const handleSelectModelSet = modelSet => {
    onSelectModelSet(modelSet);
    handleClose();
  };

  const handleSelectTritonModel = tritonRow => {
    if (onSelectTritonModel) onSelectTritonModel(tritonRow);
    handleClose();
  };

  const handleTabsChange = (event, value) => {
    event.persist && event.persist();
    dispatch(setSelectModelTab(value));
  };

  return (
    <Modal onClose={handleClose} open={open}>
      <Card {...rest} className={classes.root}>
        <CardHeader title="Select Model" />
        <Divider />

        <CardContent className={classes.content}>
          <div className={classes.searchContainer}>
            <Grid container spacing={3}>
              <Grid item>
                <Search
                  autoFocus={true}
                  placeholder="Name"
                  value={search}
                  onSearchValueChange={handleSearchFieldChange}
                  className={classes.search}
                  hideSearchButton={true}
                />
              </Grid>
            </Grid>
          </div>
          <Divider />
          {((tab === 'models' && loadingModels) ||
            (tab === 'modelSets' && loadingModelSets) ||
            (tab === 'triton' && loadingTritonModels)) && <LinearProgress />}

          {(modelSetsSupported || tritonModelsSupported) && (
            <>
              <Tabs
                className={classes.tabs}
                onChange={handleTabsChange}
                value={tab}>
                {tabs.map(tab => (
                  <Tab
                    className={classes.tab}
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                  />
                ))}
              </Tabs>
              <Divider />
            </>
          )}
          <div className={classes.tableContainer}>
            {tab === 'models' && (
              <TableContainer className={classes.container}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '0%' }} />
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchedModels.map(model => (
                      <TableRow key={model.id} hover>
                        <TableCell className={classes.buttonCell}>
                          <Button
                            className={classes.buttonExtraSmall}
                            size="small"
                            variant="outlined"
                            onClick={() => handleSelectModel(model)}>
                            Select
                          </Button>
                        </TableCell>
                        <TableCell>{model.displayName}</TableCell>
                        <TableCell className={classes.labelCell}>
                          <ModelConfigurationProcessingStatus
                            modelConfiguration={model}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loadingModels && searchedModels.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {tab === 'modelSets' && (
              <TableContainer className={classes.container}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '0%' }} />
                      <TableCell>Name</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Models</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchedModelSets.map(modelSet => (
                      <TableRow key={modelSet.id} hover>
                        <TableCell className={classes.buttonCell}>
                          <Button
                            className={classes.buttonExtraSmall}
                            size="small"
                            variant="outlined"
                            onClick={() => handleSelectModelSet(modelSet)}>
                            Select
                          </Button>
                        </TableCell>
                        <TableCell>{modelSet.name}</TableCell>
                        <TableCell>
                          {
                            MODEL_SET_CALUCLATION_METHODS[
                              modelSet.calculationMethod
                            ]
                          }
                        </TableCell>
                        <TableCell>
                          {modelSet.models
                            .map(m => {
                              if (m.model && m.model.displayName) {
                                return (
                                  m.model.displayName +
                                  ' (' +
                                  m.weight * 100 +
                                  '%)'
                                );
                              }
                              if (!m.tritonTypeName) return '';
                              /* Reuse the Triton types the picker already fetched
                                 to resolve aliases — no second request needed. */
                              const alias = (eligibleTritonTypes || [])
                                .find(t => t.typeName === m.tritonTypeName)
                                ?.versions?.find(
                                  v => v.integerVersion === m.tritonVersion
                                )?.alias;
                              return (
                                (alias ||
                                  m.tritonTypeName + ' v' + m.tritonVersion) +
                                ' (' +
                                m.weight * 100 +
                                '%)'
                              );
                            })
                            .join(', ')}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loadingModelSets && searchedModelSets.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {tab === 'triton' && (
              <TableContainer className={classes.container}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '0%' }} />
                      <TableCell>Model</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Version</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchedTritonRows.map(row => (
                      <TableRow key={row.identity} hover>
                        <TableCell className={classes.buttonCell}>
                          <Button
                            className={classes.buttonExtraSmall}
                            size="small"
                            variant="outlined"
                            onClick={() => handleSelectTritonModel(row)}>
                            Select
                          </Button>
                        </TableCell>
                        <TableCell>
                          {row.alias || row.typeName + ' v' + row.version}
                        </TableCell>
                        <TableCell>{row.typeName}</TableCell>
                        <TableCell>v{row.version}</TableCell>
                      </TableRow>
                    ))}
                    {!loadingTritonModels && searchedTritonRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            onClick={() => {
              handleSelectModel(null);
              handleSelectModelSet(null);
              if (onSelectTritonModel) handleSelectTritonModel(null);
            }}
            variant="contained">
            Clear Selection
          </Button>
          <Button onClick={handleClose} variant="contained">
            Cancel
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

SelectModelModal.displayName = 'SelectModel';

SelectModelModal.propTypes = {
  className: PropTypes.string,
  modelSetsSupported: PropTypes.bool,
  tritonModelsSupported: PropTypes.bool,
  onSelectModel: PropTypes.func,
  onSelectModelSet: PropTypes.func,
  onSelectTritonModel: PropTypes.func,
  excludedModelIds: PropTypes.arrayOf(PropTypes.string),
  excludedModelSetIds: PropTypes.arrayOf(PropTypes.string),
  excludedTritonIds: PropTypes.arrayOf(PropTypes.string)
};

export default SelectModelModal;
